/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer, useCallback } from "react";
import { useOutletContext, useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { Save, ArrowBigRight, Pencil, Eye, Crown, FilePlus2, Filter, ChefHat, UserCheck, Trash, Eraser } from "lucide-react";
import { useAdminGetForum, useAdminGetForumRights, useAdminUpdateForumRights } from "@/services/forumService";
import { useAdminGetRolesSWR, useAdminGetRolesDefinitionsSWR } from "@/services/roleService";
import { useAdminGetSkillsSWR } from "@/services/skillService";
import { useAdminGetCompetitionsSWR } from "@/services/competitionService";



const textColors = { "blue": "text-blue-500", "green": "text-green-500", "red": "text-red-500", "orange": "text-orange-500", "black": "text-primary" }
const borderColors = { "blue": "border-blue-500", "green": "border-green-500", "red": "border-red-500", "orange": "border-orange-500", "black": "border-primary" }

const textColorsLight = { "blue": "text-blue-200", "green": "text-green-200", "red": "text-red-200", "orange": "text-orange-200", "black": "text-secondary" }
const borderColorsLight = { "blue": "border-blue-200", "green": "border-green-200", "red": "border-red-200", "orange": "border-orange-200", "black": "border-secondary" }

const initialState = {
    initialApiPayload: [],
    apiPayload: [],
    role_filter: {
        type: '',
        skill_id: '',
        competition_id: '',
    },
    selected_role: "",
    selected_all_roles: false,
    selected_right: "",
    forum: {},
    error_message: "",
    isLoading: false
}



const ACTIONS = {
    SET_INITIAL_API_PAYLOAD: 'setInitialApiPayload',
    SET_API_PAYLOAD_FIELD: 'setApiPayloadField',
    SET_FORUM: 'setForumName',
    ADD_RIGHT: 'addRight',
    ADD_RIGHT_ALL_ROLES: 'addRightAllRoles',
    REMOVE_RIGHT: 'removeRight',
    REMOVE_ALL_RIGHTS: 'removeAllRights',
    SET_ERROR_MESSAGE: 'setErrorMessage',
    SET_IS_LOADING: 'setIsLoading',
    SET_ROLE_FILTER: 'setRoleFilter',
    SET_SELECTED_ROLE: 'setSelectedRole',
    SET_SELECTED_ALL_ROLES: 'setSelectedAllRoles',
    SET_SELECTED_RIGHT: 'setSelectedRight',
}



const droits = [ "topic_create", "forum_read", "forum_write", "forum_moderate", "forbidden" ]



const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_INITIAL_API_PAYLOAD:
            return { ...state, apiPayload: action.payload, initialApiPayload: action.payload, error_message: null }
        case ACTIONS.SET_API_PAYLOAD_FIELD:
            return { ...state, apiPayload: { ...state.apiPayload, [action.subtype]: action.payload }, error_message: null }
        case ACTIONS.SET_FORUM:
            return { ...state, forum: action.payload }
        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, error_message: action.payload }
        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload }
        case ACTIONS.SET_ROLE_FILTER:
            return { ...state, role_filter: { ...state.role_filter, [action.subtype]: action.payload } }
        case ACTIONS.SET_SELECTED_ROLE:
            return { ...state, selected_role: action.payload }
        case ACTIONS.SET_SELECTED_ALL_ROLES:
            return { ...state, selected_all_roles: action.payload }
        case ACTIONS.SET_SELECTED_RIGHT:
            return { ...state, selected_right: action.payload }
        case ACTIONS.ADD_RIGHT:
            return { 
                ...state, 
                apiPayload: [...state.apiPayload, { role_id: Number(state.selected_role), right: state.selected_right }], 
                selected_role: '', 
                selected_right: ''  
            }
        case ACTIONS.REMOVE_RIGHT:
            return { ...state, apiPayload: state.apiPayload.filter((right) => right.role_id != Number(action.payload)) }
        case ACTIONS.ADD_RIGHT_ALL_ROLES: {
            const result = action.payload.map((role) => {
                return { role_id: role.id, right: state.selected_right }
            })
            return { ...state, apiPayload: state.apiPayload.concat( result ) }
        }
        case ACTIONS.REMOVE_ALL_RIGHTS:
            return { ...state, apiPayload: [] }
        default:
            throw new Error("Unknown action.type " + action.type);
    }
}



export default function Rights() {

    const [ , setPageTitle,  , setShowBackButton, , setShowTitle] = useOutletContext()
    const [state, dispatch] = useReducer(reducer, initialState)

    const navigate = useNavigate()
    const location = useLocation()
    const {id} = useParams()

    const adminGetForum = useAdminGetForum()
    const adminGetForumRights = useAdminGetForumRights()
    const adminUpdateForumRights = useAdminUpdateForumRights()
    const {data: roles } = useAdminGetRolesSWR()
    const {data: rolesDefinitions } = useAdminGetRolesDefinitionsSWR()
    const {data: skills } = useAdminGetSkillsSWR()
    const {data: competitions } = useAdminGetCompetitionsSWR()


    /* On met à jour le titre */
    useEffect(() => {
        setShowTitle(false)
        setPageTitle("Gestion des droits d'un forum")
        setShowBackButton(true)

        return () => {
            setPageTitle("Titre")
            setShowBackButton(false)
            setShowTitle(true)
        }
    }, []);



    /* On met à jour le titre du formulaire lorsque les informations du forum sont disponibles */
    useEffect(() => {
        setPageTitle(`Gestion des droits d'un forum : ${state?.forum.name}`)
    }, [state?.forum?.name]);



    /* On récupère le forum automatiquement lorsque l'id est disponible */
    useEffect(() => {
        const getForum = async () => {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

            try {
                const forum = await adminGetForum(id)
                dispatch({ type: ACTIONS.SET_FORUM, payload: forum })

                const rights = await adminGetForumRights(id)
                dispatch({ type: ACTIONS.SET_INITIAL_API_PAYLOAD, payload: rights })

            } catch (error) {
                console.error('admin/forums/[id]/rights::getForum::error', error)
                if (isAxiosError(error)) {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
                } else {
                    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
                }
            } finally {
                dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
           }
        }

        getForum()
    }, [id])



    /* Création d'une fonction de filtre des rôle possibles */
    const filterRoles = useCallback(() => {
        return roles?.filter((role) => { 
            let result = true
            if(state?.role_filter?.type && state?.role_filter?.type.length > 0) 
                result &&= role.type === state.role_filter.type

            if (state?.role_filter?.skill_id && state?.role_filter?.skill_id.length > 0)
                result &&= role.skill_id === Number(state.role_filter.skill_id)

            if (state?.role_filter?.competition_id && state?.role_filter?.competition_id.length > 0)
                result &&= role.competition_id === Number(state.role_filter.competition_id)

            if (state?.apiPayload?.filter( (current_right) => current_right.role_id == role.id ).length > 0) result = false
            
            if( state?.apiPayload?.role_ids?.includes(role.id)) result = false

            return result
        })
    }, [roles, state.role_filter, state.apiPayload])



    /* Gestion du bouton de soumission */
    const handleSubmit = async (e) => {
        e.preventDefault()

        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });

        try {
            await adminUpdateForumRights(id, state.apiPayload)
            navigate(location.state?.from ?? "/admin/forums")
        } catch (error) {
            console.error('admin/forums/add::handleSubmit::error', error)
            if (error?.response?.status === 412) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Erreur de validation en base de données (code déjà utilisé, contrainte non respectée)" });
            } else if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }

    }

    return (
        <>  
            <h1 className="text-primary text-xl flex-none content-center mb-4">
                Droits spécifiques au forum : {state?.forum?.name}
            </h1>
            <p className="text-secondary italic mb-4">Les droits spécifiques au forum écrasent les droits assignés par les groupes de forums</p>
            <div className="w-full flex flex-row gap-5 content-stretch">
                <div className="flex flex-col flex-1 border border-secondary rounded-md px-2 py-1 bg-gray-100 gap-3">
                    <div className="flex flex-row items-center gap-2">
                        <Filter size={16} className="text-secondary" />
                        <h3 className="text-primary">Filtre des rôles possibles</h3>
                    </div>
                    <div className="w-full flex flex-col items-left text-sm gap-1">
                        <div className="flex flex-col flex-grow basis-1/3 gap-2 flex-1 text-secondary">
                            <span>Type :</span>
                            <select className="border-1 border-gray-400 py-1 text-sm text-secondary"
                                onChange={(e) => dispatch({ type: ACTIONS.SET_ROLE_FILTER, subtype: "type", payload: e.target.value })} 
                                value={state.role_filter.type}>

                                <option value="">-</option>
                                {rolesDefinitions && Object.keys(rolesDefinitions)?.map((role, i) => {
                                    return (
                                        <option key={i} value={role}>
                                            {role}
                                        </option>
                                    )
                                })}
                            </select>
                        </div>
                        <div className="flex flex-col flex-grow basis-1/3 gap-2 text-secondary">
                            <span>Métier :</span>
                            <select className="border-1 border-gray-400 py-1 text-sm text-secondary"
                                onChange={(e) => dispatch({ type: ACTIONS.SET_ROLE_FILTER, subtype: "skill_id", payload: e.target.value })} 
                                value={state.role_filter.skill_id}>

                                <option value="">-</option>
                                {skills?.map((skill, i) => {
                                    return (
                                        <option key={i} value={skill.id}>
                                            {skill.code} - {skill.name}
                                        </option>
                                    )
                                })}
                            </select>
                        </div>
                        <div className="flex flex-col flex-grow basis-1/3 gap-2 text-secondary">
                            <span>Compétition :</span>
                            <select className="border-1 border-gray-400 py-1 text-sm text-secondary"
                                onChange={(e) => dispatch({ type: ACTIONS.SET_ROLE_FILTER, subtype: "competition_id", payload: e.target.value })} 
                                value={state.role_filter.competition_id}>

                                <option value="">-</option>
                                {competitions?.map((competition, i) => {
                                    return (
                                        <option key={i} value={competition.id}>
                                            {competition.code} - {competition.name}
                                        </option>
                                    )
                                })}
                            </select>
                        </div>
                    </div>
                    <div className="flex flex-row items-center gap-2">
                        <ChefHat size={16} className="text-secondary" />
                        <h3 className="text-secondary">Rôles possibles</h3>
                    </div>
                    <div className="w-full flex flex-row items-center text-sm">
                        <div className="text-primary flex flex-row flex-wrap gap-1 items-stretch">
                            <select className="flex-1 border-1 border-gray-500 py-1 text-base text-primary"
                                onChange={(e) => dispatch({ type: ACTIONS.SET_SELECTED_ROLE, payload: e.target.value })} 
                                value={state.selected_role}
                                disabled={state.selected_all_roles}
                            >
                                <option value="">-</option>
                                {filterRoles()?.map((role, i) => {
                                    return (
                                        <option key={i} value={role.id}>
                                            {role.name}
                                        </option>
                                    )
                                })}
                            </select>

                            <div className="flex flex-row items-center gap-2">
                                <input type="checkbox" name="all_roles" className="h-4 w-4"
                                    onChange={(e) => dispatch({ type: ACTIONS.SET_SELECTED_ALL_ROLES, payload: e.target.checked })}
                                    checked={state.selected_all_roles}
                                />
                                <label className="block text-sm font-medium text-gray-700" htmlFor="all_roles">Tous</label>
                            </div>

                            { (state?.selected_role || state?.selected_all_roles) && <select className="flex-1 border-1 border-gray-500 py-1 text-base text-primary"
                                onChange={(e) => {dispatch({ type: ACTIONS.SET_SELECTED_RIGHT, payload: e.target.value })}}
                                value={state.selected_right}
                            >
                                <option value="">-</option>
                                {
                                    droits.map((right, i) => {
                                        return (
                                            <option key={i} value={right}>
                                                {right}
                                            </option>
                                        )
                                    })
                                }
                                </select>
                            }
                        </div>
                        <div className="items-center">
                            { (state?.selected_role) && state?.selected_right && <button
                                onClick={(e) => {e.preventDefault(); dispatch({ type: ACTIONS.ADD_RIGHT })}}
                            >
                                <ArrowBigRight size={24} className="text-primary hover:text-blue-600" />
                            </button> }
                            { (state?.selected_all_roles) && state?.selected_right && <button
                                onClick={(e) => {e.preventDefault(); dispatch({ type: ACTIONS.ADD_RIGHT_ALL_ROLES, payload: filterRoles() })}}
                            >
                                <ArrowBigRight size={24} className="text-primary hover:text-blue-600" />
                            </button> }
                        </div>
                    </div>

                </div>
                <div className="flex flex-1 flex-col border border-secondary rounded-md px-2 py-1 bg-gray-100 gap-3">
                    <div className="flex flex-row items-center gap-2">
                        <UserCheck size={16} className="text-secondary" />
                        <h3 className="text-primary">Droits spécifiques appliqués</h3>
                        { state.apiPayload.length > 0 && (
                            <button className="flex gap-2 items-center px-2 py-1 text-base text-red-400 hover:text-red-600"
                                onClick={(e) => {e.preventDefault(); dispatch({ type: ACTIONS.REMOVE_ALL_RIGHTS })}}
                                title="Supprimer tous les droits"
                            >
                                <Eraser size={14} className="" />                            
                            </button>
                        )}
                    </div>
                    
                    <div className="w-full flex flex-row flex-wrap gap-2">
                        {
                            state.apiPayload.map((right, i) => {

                                let color = "black"
                                if (right.right == "forum_read") color = "blue"
                                if (right.right == "forum_write") color = "green"
                                if (right.right == "forum_moderate") color = "red"
                                if (right.right == "topic_create") color = "orange"

                                return <div key={i} className={`p-1 rounded-lg border ${borderColors[color]} flex flex-row items-center gap-1 text-primary`}>
                                    
                                    {right.right == "forum_read" && <Eye size={16} className={textColors[color]} />}
                                    {right.right == "forum_write" && <Pencil size={16} className={textColors[color]} />}
                                    {right.right == "forum_moderate" && <Crown size={16} className={textColors[color]} />}
                                    {right.right == "topic_create" && <FilePlus2 size={16} className={textColors[color]} />}

                                    {roles?.find((role) => role.id === right.role_id)?.name || "role : " + right.role_id}
                                    
                                    <Trash size={16} className={`cursor-pointer text-red-600 hover:text-red-700`} onClick={(e) => {e.preventDefault();dispatch({ type: ACTIONS.REMOVE_RIGHT, payload: right.role_id })}} />
                                </div>
                            })
                        }
                        
                    </div>
                    <div className="flex flex-row items-center gap-2">
                        <UserCheck size={16} className="text-secondary" />
                        <h3 className="text-secondary">Droits effectifs actuels</h3>
                    </div>
                    <div className="w-full flex flex-row flex-wrap gap-2">
                        {
                            state?.forum?.rights?.map((right, i) => {
                                let color = "black"
                                if (right.right == "forum_read") color = "blue"
                                if (right.right == "forum_write") color = "green"
                                if (right.right == "forum_moderate") color = "red"
                                if (right.right == "topic_create") color = "orange"

                                return <div key={i} className={`p-1 rounded-lg border ${borderColorsLight[color]} flex flex-row items-center gap-1 text-secondary text-xs`}>
                                    
                                    {right.right == "forum_read" && <Eye size={16} className={textColorsLight[color]} />}
                                    {right.right == "forum_write" && <Pencil size={16} className={textColorsLight[color]} />}
                                    {right.right == "forum_moderate" && <Crown size={16} className={textColorsLight[color]} />}
                                    {right.right == "topic_create" && <FilePlus2 size={16} className={textColorsLight[color]} />}

                                    {roles?.find((role) => role.id === right.role_id)?.name || "role : " + right.role_id}
                                    
                                </div>
                            })
                        }
                    </div>
                </div>
            </div>
            <div className="w-full flex flex-row gap-5 content-stretch pt-4">
                <button className="flex gap-2 items-center border border-blue-500 rounded-md px-2 py-1 text-base hover:bg-blue-200"
                    onClick={(e) => handleSubmit(e)}
                    disabled={state.isLoading}
                >
                    <Save size={24} className="text-primary" />
                    Sauvegarder
                </button>
                <Link to={`/admin/forums/${id}`}
                    className="flex gap-2 items-center border border-gray-500 rounded-md px-2 py-1 text-base hover:bg-gray-200"
                >
                    Retour
                </Link>
                {
                    state?.error_message && <span className="text-red-700 flex-none content-center mr-4">{state.error_message}</span>
                }
            </div>
        </>
    )
}